import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../../Common/Navbar";
import "./UserHostingSetup.css";
import { connectRealtimeSocket } from "../CompleteCPanel/utils/realtimeSocket";

/* ==============================
   API BASE
============================== */
const normalizeBase = (value) => String(value || "").trim().replace(/\/+$/, "");

const ensureApiSuffix = (base) => {
  const clean = normalizeBase(base);
  if (!clean) return "http://localhost:5000/api";
  return clean.endsWith("/api") ? clean : `${clean}/api`;
};

const API_BASE = ensureApiSuffix(
  process.env.REACT_APP_API_BASE || "http://localhost:5000"
);

/* ==============================
   AUTH HELPERS
============================== */
const AUTH_TOKEN_KEYS = [
  "token",
  "authToken",
  "accessToken",
  "userToken",
  "jwtToken",
];

const AUTH_CLEAR_KEYS = [
  ...AUTH_TOKEN_KEYS,
  "isLoggedIn",
  "user",
  "currentUser",
  "email",
  "userEmail",
  "user_email",
  "fullName",
  "name",
  "userName",
  "username",
];

const getStorageValue = (key) => {
  return localStorage.getItem(key) || sessionStorage.getItem(key) || "";
};

const getToken = () => {
  for (const key of AUTH_TOKEN_KEYS) {
    const value = getStorageValue(key);

    if (value && String(value).trim()) {
      return String(value).trim();
    }
  }

  return "";
};

const clearAuth = () => {
  AUTH_CLEAR_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });

  window.dispatchEvent(
    new CustomEvent("auth-state-changed", {
      detail: {
        source: "UserHostingSetup",
        action: "clear-auth",
      },
    })
  );
};

const safeJson = async (response) => {
  try {
    return await response.json();
  } catch {
    return {};
  }
};

/* ==============================
   HELPERS
============================== */
const initialForm = {
  domainName: "",
  websiteType: "business",
  businessName: "",
  needEmail: false,
  needSsl: true,
  notes: "",
};

const websiteTypes = [
  { value: "business", label: "Business Website" },
  { value: "portfolio", label: "Portfolio Website" },
  { value: "ecommerce", label: "Ecommerce Store" },
  { value: "blog", label: "Blog / News Website" },
  { value: "landing", label: "Landing Page" },
  { value: "custom", label: "Custom Website" },
];

const setupStatusLabels = {
  pending_setup: "Pending Setup",
  in_progress: "In Progress",
  completed: "Completed",
  failed: "Failed",
};

const requestStatusLabels = {
  submitted: "Submitted",
  in_progress: "In Progress",
  completed: "Completed",
  rejected: "Rejected",
};

const normalizeDomainName = (value = "") => {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\/.*$/, "")
    .replace(/\s+/g, "");
};

const isValidDomain = (domain = "") => {
  return /^(?!-)([a-z0-9-]{1,63}\.)+[a-z]{2,}$/i.test(domain);
};

const getSetupLabel = (value = "") => {
  return setupStatusLabels[value] || "Pending Setup";
};

const getRequestLabel = (value = "") => {
  return requestStatusLabels[value] || "Submitted";
};

const getStatusClass = (value = "") => {
  const clean = String(value || "").toLowerCase();

  if (clean === "completed") return "completed";
  if (clean === "in_progress") return "progress";
  if (clean === "failed" || clean === "rejected") return "failed";
  if (clean === "submitted") return "submitted";

  return "pending";
};

const normalizeHostingPlan = (payload = {}) => {
  const data = payload?.data || payload || {};
  const service = payload?.service || data?.service || null;

  return {
    hasActivePlan: Boolean(payload?.hasActivePlan || data?.planName || service),
    planKey: data?.planKey || service?.planKey || service?.plan_key || "",
    planName: data?.planName || service?.planName || service?.plan_name || "",
    price: Number(data?.price || service?.rawPrice || service?.price || 0),
    billingCycle:
      data?.billingCycle || service?.billingCycle || service?.billing_cycle || "monthly",
    status: data?.status || service?.status || "active",
    setupStatus:
      data?.setupStatus ||
      service?.setupStatus ||
      service?.setup_status ||
      "pending_setup",
    websiteLimit:
      Number(
        service?.websiteLimit ||
          service?.website_limit ||
          data?.limits?.websites ||
          0
      ) || 0,
    storageLimit:
      service?.storageLimit ||
      service?.storage_limit ||
      data?.limits?.storage ||
      "",
    mailboxLimit:
      Number(
        service?.mailboxLimit ||
          service?.mailbox_limit ||
          data?.limits?.mailboxes ||
          0
      ) || 0,
    sslEnabled: Boolean(
      service?.sslEnabled !== undefined
        ? service.sslEnabled
        : service?.ssl_enabled !== undefined
        ? service.ssl_enabled
        : data?.limits?.ssl
    ),
    cpanelUrl: service?.cpanelUrl || service?.cpanel_url || "",
    cpanelUsername: service?.cpanelUsername || service?.cpanel_username || "",
    serverIp: service?.serverIp || service?.server_ip || "",
    nameserver1: service?.nameserver1 || service?.nameserver_1 || "",
    nameserver2: service?.nameserver2 || service?.nameserver_2 || "",
    expiresAt: data?.expiresAt || service?.expiresAt || service?.expires_at || "",
    service,
  };
};

const normalizeSetupRequest = (request = null) => {
  if (!request || typeof request !== "object") return null;

  return {
    id: request.id || null,
    serviceId: request.serviceId || request.service_id || null,
    domainName: request.domainName || request.domain_name || "",
    websiteType: request.websiteType || request.website_type || "business",
    businessName: request.businessName || request.business_name || "",
    needEmail: Boolean(
      request.needEmail !== undefined ? request.needEmail : request.need_email
    ),
    needSsl: Boolean(
      request.needSsl !== undefined ? request.needSsl : request.need_ssl
    ),
    notes: request.notes || "",
    status: request.status || "submitted",
    createdAtFormatted: request.createdAtFormatted || "",
    updatedAtFormatted: request.updatedAtFormatted || "",
  };
};

function UserHostingSetup() {
  const navigate = useNavigate();

  const [hostingPlan, setHostingPlan] = useState(null);
  const [setupRequest, setSetupRequest] = useState(null);
  const [formData, setFormData] = useState(initialForm);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [realtimeStatus, setRealtimeStatus] = useState("connecting");

  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const authFetch = useCallback(
    async (endpoint, options = {}) => {
      const token = getToken();

      if (!token) {
        clearAuth();
        navigate("/signin", { replace: true });
        throw new Error("Session expired. Please login again.");
      }

      const response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        cache: "no-store",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          ...(options.headers || {}),
        },
      });

      const data = await safeJson(response);

      if (response.status === 401 || response.status === 403) {
        clearAuth();
        navigate("/signin", { replace: true });
        throw new Error("Session expired. Please login again.");
      }

      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || "Request failed.");
      }

      return data;
    },
    [navigate]
  );

  const loadSetupData = useCallback(
    async ({ silent = false } = {}) => {
      try {
        if (!silent) {
          setLoading(true);
        } else {
          setRefreshing(true);
        }

        setError("");

        const [planResult, setupResult] = await Promise.all([
          authFetch("/hosting/plan"),
          authFetch("/hosting/setup/my"),
        ]);

        const nextPlan = normalizeHostingPlan(planResult);
        const nextRequest = normalizeSetupRequest(setupResult?.data || null);

        setHostingPlan(nextPlan);
        setSetupRequest(nextRequest);

        if (nextRequest) {
          setFormData({
            domainName: nextRequest.domainName || "",
            websiteType: nextRequest.websiteType || "business",
            businessName: nextRequest.businessName || "",
            needEmail: nextRequest.needEmail || false,
            needSsl: nextRequest.needSsl !== false,
            notes: nextRequest.notes || "",
          });
        }
      } catch (err) {
        setError(err.message || "Unable to load hosting setup.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [authFetch]
  );

  useEffect(() => {
    loadSetupData();
  }, [loadSetupData]);

  useEffect(() => {
    const socket = connectRealtimeSocket();

    if (!socket) {
      setRealtimeStatus("offline");
      return undefined;
    }

    let refreshTimer = null;

    const softRefresh = () => {
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => {
        loadSetupData({ silent: true });
      }, 600);
    };

    const handleConnected = () => {
      setRealtimeStatus("connected");
    };

    const handleDisconnected = () => {
      setRealtimeStatus("offline");
    };

    const handleRefresh = () => {
      setRealtimeStatus("connected");
      softRefresh();
    };

    const handleAuthError = () => {
      clearAuth();
      navigate("/signin", { replace: true });
    };

    socket.on("connect", handleConnected);
    socket.on("disconnect", handleDisconnected);
    socket.on("socket:connected", handleConnected);
    socket.on("hosting:status", handleRefresh);
    socket.on("dashboard:update", handleRefresh);
    socket.on("notification:new", handleRefresh);
    socket.on("activity:new", handleRefresh);
    socket.on("connect_error", handleAuthError);

    if (socket.connected) handleConnected();

    return () => {
      clearTimeout(refreshTimer);

      socket.off("connect", handleConnected);
      socket.off("disconnect", handleDisconnected);
      socket.off("socket:connected", handleConnected);
      socket.off("hosting:status", handleRefresh);
      socket.off("dashboard:update", handleRefresh);
      socket.off("notification:new", handleRefresh);
      socket.off("activity:new", handleRefresh);
      socket.off("connect_error", handleAuthError);
    };
  }, [loadSetupData, navigate]);

  const isCompleted = hostingPlan?.setupStatus === "completed";
  const hasActivePlan = Boolean(hostingPlan?.hasActivePlan);

  const setupBadge = useMemo(() => {
    if (!hasActivePlan) return "No Active Hosting";
    return getSetupLabel(hostingPlan?.setupStatus || "pending_setup");
  }, [hasActivePlan, hostingPlan?.setupStatus]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    setError("");
    setMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const cleanDomain = normalizeDomainName(formData.domainName);

    if (!cleanDomain) {
      setError("Domain name is required.");
      return;
    }

    if (!isValidDomain(cleanDomain)) {
      setError("Please enter a valid domain name, for example example.com.");
      return;
    }

    if (formData.businessName.trim().length > 180) {
      setError("Business name is too long.");
      return;
    }

    if (formData.notes.trim().length > 1500) {
      setError("Notes must be under 1500 characters.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      setMessage("");

      const result = await authFetch("/hosting/setup/request", {
        method: "POST",
        body: JSON.stringify({
          domainName: cleanDomain,
          websiteType: formData.websiteType,
          businessName: formData.businessName.trim(),
          needEmail: formData.needEmail,
          needSsl: formData.needSsl,
          notes: formData.notes.trim(),
        }),
      });

      const nextRequest = normalizeSetupRequest(result?.data || null);

      setSetupRequest(nextRequest);
      setMessage(
        result?.message ||
          "Hosting setup request submitted successfully. Our team will process it shortly."
      );

      await loadSetupData({ silent: true });
    } catch (err) {
      setError(err.message || "Unable to submit hosting setup request.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Navbar />

      <section className="user-hosting-setup-page">
        <div className="user-hosting-setup-container">
          <div className="uhs-hero">
            <div>
              <span className="uhs-eyebrow">Hosting Setup</span>

              <h1>Complete Your Hosting Setup</h1>

              <p>
                Submit your domain and website details. Your hosting plan is
                already active after payment. This setup request helps us connect
                your domain, SSL, email and server access correctly.
              </p>

              <div className="uhs-meta-row">
                <span>
                  {realtimeStatus === "connected"
                    ? "🟢 Realtime connected"
                    : realtimeStatus === "connecting"
                    ? "🟡 Connecting realtime"
                    : "⚪ Realtime offline"}
                </span>

                <span className={`uhs-status ${getStatusClass(hostingPlan?.setupStatus)}`}>
                  {setupBadge}
                </span>
              </div>
            </div>

            <div className="uhs-hero-actions">
              <button
                type="button"
                onClick={() => loadSetupData({ silent: true })}
                disabled={loading || refreshing}
              >
                {refreshing ? "Refreshing..." : "Refresh"}
              </button>

              <Link to="/dashboard">Dashboard</Link>
            </div>
          </div>

          {error ? <div className="uhs-alert error">{error}</div> : null}
          {message ? <div className="uhs-alert success">{message}</div> : null}

          {!loading && !hasActivePlan ? (
            <div className="uhs-empty-card">
              <h2>No active hosting plan found</h2>
              <p>
                Please choose and activate a hosting plan first. After payment,
                you can complete your hosting setup here.
              </p>
              <Link to="/hosting">Choose Hosting Plan</Link>
            </div>
          ) : null}

          {loading ? (
            <div className="uhs-loading-card">Loading hosting setup...</div>
          ) : hasActivePlan ? (
            <div className="uhs-layout">
              <div className="uhs-left">
                <div className="uhs-plan-card">
                  <div className="uhs-plan-head">
                    <div>
                      <span className="uhs-card-tag">Active Plan</span>
                      <h2>{hostingPlan?.planName || "Hosting Plan"}</h2>
                      <p>
                        ₹{hostingPlan?.price || 0} /{" "}
                        {hostingPlan?.billingCycle || "monthly"}
                      </p>
                    </div>

                    <span
                      className={`uhs-status ${getStatusClass(
                        hostingPlan?.setupStatus
                      )}`}
                    >
                      {getSetupLabel(hostingPlan?.setupStatus)}
                    </span>
                  </div>

                  <div className="uhs-plan-grid">
                    <div>
                      <strong>Websites</strong>
                      <span>{hostingPlan?.websiteLimit || 0}</span>
                    </div>

                    <div>
                      <strong>Storage</strong>
                      <span>{hostingPlan?.storageLimit || "Included"}</span>
                    </div>

                    <div>
                      <strong>Mailboxes</strong>
                      <span>{hostingPlan?.mailboxLimit || 0}</span>
                    </div>

                    <div>
                      <strong>SSL</strong>
                      <span>
                        {hostingPlan?.sslEnabled ? "Included" : "Not included"}
                      </span>
                    </div>

                    <div>
                      <strong>Expires</strong>
                      <span>{hostingPlan?.expiresAt || "Not available"}</span>
                    </div>
                  </div>

                  {isCompleted ? (
                    <div className="uhs-access-card">
                      <h3>Hosting Access Ready</h3>

                      <div className="uhs-access-grid">
                        <div>
                          <strong>cPanel Username</strong>
                          <span>{hostingPlan?.cpanelUsername || "Not set"}</span>
                        </div>

                        <div>
                          <strong>Server IP</strong>
                          <span>{hostingPlan?.serverIp || "Not set"}</span>
                        </div>

                        <div>
                          <strong>Nameserver 1</strong>
                          <span>{hostingPlan?.nameserver1 || "Not set"}</span>
                        </div>

                        <div>
                          <strong>Nameserver 2</strong>
                          <span>{hostingPlan?.nameserver2 || "Not set"}</span>
                        </div>
                      </div>

                      {hostingPlan?.cpanelUrl ? (
                        <a
                          href={hostingPlan.cpanelUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="uhs-primary-link"
                        >
                          Open cPanel
                        </a>
                      ) : null}
                    </div>
                  ) : (
                    <div className="uhs-note-card">
                      Your plan is active. Complete the setup form to start
                      domain/server configuration.
                    </div>
                  )}
                </div>

                {setupRequest ? (
                  <div className="uhs-request-card">
                    <div className="uhs-request-head">
                      <div>
                        <span className="uhs-card-tag">Latest Request</span>
                        <h3>{setupRequest.domainName}</h3>
                      </div>

                      <span
                        className={`uhs-status ${getStatusClass(
                          setupRequest.status
                        )}`}
                      >
                        {getRequestLabel(setupRequest.status)}
                      </span>
                    </div>

                    <div className="uhs-request-list">
                      <p>
                        <strong>Website Type:</strong>{" "}
                        {setupRequest.websiteType}
                      </p>
                      <p>
                        <strong>Business:</strong>{" "}
                        {setupRequest.businessName || "Not provided"}
                      </p>
                      <p>
                        <strong>Email:</strong>{" "}
                        {setupRequest.needEmail ? "Required" : "Not required"}
                      </p>
                      <p>
                        <strong>SSL:</strong>{" "}
                        {setupRequest.needSsl ? "Required" : "Not required"}
                      </p>
                      <p>
                        <strong>Submitted:</strong>{" "}
                        {setupRequest.createdAtFormatted || "Recently"}
                      </p>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="uhs-right">
                <form className="uhs-form-card" onSubmit={handleSubmit}>
                  <span className="uhs-card-tag">User Setup Form</span>

                  <h2>{setupRequest ? "Update Setup Request" : "Start Setup"}</h2>

                  <p>
                    Enter the domain and website information you want to connect
                    with your active hosting plan.
                  </p>

                  <label>
                    Domain Name
                    <input
                      type="text"
                      name="domainName"
                      placeholder="example.com"
                      value={formData.domainName}
                      onChange={handleInputChange}
                      disabled={isCompleted || saving}
                    />
                  </label>

                  <label>
                    Website Type
                    <select
                      name="websiteType"
                      value={formData.websiteType}
                      onChange={handleInputChange}
                      disabled={isCompleted || saving}
                    >
                      {websiteTypes.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label>
                    Business / Project Name
                    <input
                      type="text"
                      name="businessName"
                      placeholder="Your business name"
                      value={formData.businessName}
                      onChange={handleInputChange}
                      disabled={isCompleted || saving}
                    />
                  </label>

                  <div className="uhs-check-grid">
                    <label className="uhs-check">
                      <input
                        type="checkbox"
                        name="needEmail"
                        checked={formData.needEmail}
                        onChange={handleInputChange}
                        disabled={isCompleted || saving}
                      />
                      <span>Need business email setup</span>
                    </label>

                    <label className="uhs-check">
                      <input
                        type="checkbox"
                        name="needSsl"
                        checked={formData.needSsl}
                        onChange={handleInputChange}
                        disabled={isCompleted || saving}
                      />
                      <span>Need SSL certificate</span>
                    </label>
                  </div>

                  <label>
                    Notes
                    <textarea
                      name="notes"
                      placeholder="Any special setup instructions..."
                      value={formData.notes}
                      onChange={handleInputChange}
                      disabled={isCompleted || saving}
                    />
                  </label>

                  {isCompleted ? (
                    <div className="uhs-completed-note">
                      Your hosting setup is already completed. Contact support if
                      you need changes.
                    </div>
                  ) : (
                    <button type="submit" disabled={saving}>
                      {saving
                        ? "Submitting..."
                        : setupRequest
                        ? "Update Setup Request"
                        : "Submit Setup Request"}
                    </button>
                  )}
                </form>
              </div>
            </div>
          ) : null}
        </div>
      </section>
    </>
  );
}

export default UserHostingSetup;